"use strict"

import React, { forwardRef } from "react"
import { scaleLinear } from "d3-scale"
import { interpolateHcl } from "d3-interpolate"
import memoize from "memoize-one"
import { v4 as uuid } from "uuid"

import ObservationKeyMarker from "../GraphKeys/ObservationKeyMarker"
import { isNumber } from "../../../utils"
import { getFormattedMessage } from "../../../../components/shared_components_registration"
import SeriesPlotCanvas from "./SeriesPlotCanvas"
import AbstractPlot from "../AbstractPlot"
import SeriesLayer from "./SeriesPlotSVG"

const makeLinearScale = (domain, minRange, maxRange) => {
  return scaleLinear()
    .domain(domain)
    .range([Math.floor(minRange), Math.ceil(maxRange)])
}

class SeriesPlot extends React.Component {
  constructor(props) {
    super(props)
    this.clipID = uuid()
    this.findDataForPoint = this.findDataForPoint.bind(this)
  }

  getSeries = memoize((x, y, joinedData = [], seriesConfigs = [], timeExtractor, timeRange) => {
    const series = []

    for (let i = 0; i < joinedData.length; ++i) {
      const seriesData = joinedData[i],
        seriesConfig = seriesConfigs[i] || {}

      if (seriesData) {
        series.push({
          ...seriesConfig,
          x: (joined) => x(joined.x),
          y: (joined) => y(joined.y),
          data: seriesData,
          colour: seriesConfig.colourByTime
            ? this.getColourFunction(timeExtractor, timeRange)
            : seriesConfig.colour,
        })
      }
    }

    return series
  })

  getXScale = memoize(makeLinearScale)
  getYScale = memoize(makeLinearScale)

  getColourFunction = memoize((timeExtractor, timeRange) => {
    if (timeRange && timeRange[0] !== undefined && timeRange[1] !== undefined) {
      const scale = timeExtractor
        .buildAxis(timeRange[0], timeRange[1], "#cf0707", "#0b57a5")
        .interpolate(interpolateHcl)

      return (point) => scale(timeExtractor.getObservationTime(point))
    } else {
      return "gray"
    }
  })

  findDataForPoint(xpos, ypos) {
    const { joinedData = [], seriesConfigs, download } = this.props
    const x = this.x,
      y = this.y

    const data = []
    for (let i = 0; i < joinedData.length; ++i) {
      const seriesData = joinedData[i],
        seriesConfig = seriesConfigs[i] || {}

      if (!seriesData) continue

      let minDistance = Number.MAX_VALUE,
        bestPoint = null

      for (const point of seriesData) {
        const px = x(point.x),
          py = y(point.y)
        const dist = (px - xpos) * (px - xpos) + (py - ypos) * (py - ypos)
        if (dist < minDistance) {
          minDistance = dist
          bestPoint = point
        }
      }

      if (bestPoint) {
        data.push({
          ...bestPoint,
          label: seriesConfig.label || "???",
          marker: <ObservationKeyMarker {...seriesConfig} download={download || null} />,
        })
      }
    }
    return data
  }

  render() {
    const {
      canvasRef,
      useSVG = false,
      width = 600,
      height = 220,
      margin = {
        top: 15,
        bottom: 70,
        left: 80,
        right: 80,
      },
      titleFontSize,
      tickNum,
      joinedData = [],
      seriesConfigs = [],
      xRange = [],
      yRange = [],
      graphKey = {},
      zoomRanges = {},
      graphTitleSupplement,
      download = false,
      isSuppPlot = false,
      zoomKey,
      baseTime,
      loading = false,
      errorLayers = [],
      forceReloadPlotData,
      xAxisLabel,
      yAxisLabel,
      y2AxisLabel,
      timeExtractor,
      timeRange,
    } = this.props
    if (!joinedData || joinedData.length === 0) {
      return (
        <div style={{ width: width, height: height, margin: "5px" }}>
          {getFormattedMessage("graphing.no.data", "No data available")}
        </div>
      )
    }

    const axis1domain = graphKey.axis1domain
    const xaxisdomain = graphKey.xaxisdomain

    function getRange(range, domain_range) {
      if (!isNumber(range[0])) range[0] = 0
      if (!isNumber(range[1])) range[1] = 1

      // Give a bit of a buffer above/below the calc'd range so edge cases don't fall right on the top/bottom of
      // the graph. Makes it easier to see on plot itself. Does not overwrite zoom ranges or set domains on graph.
      const axis_range = Math.abs(range[1] - range[0])
      range[0] = range[0] - axis_range * 0.05
      range[1] = range[1] + axis_range * 0.05

      if (domain_range && range) {
        return [
          isNumber(domain_range[0]) ? domain_range[0] : range[0],
          isNumber(domain_range[1]) ? domain_range[1] : range[1],
        ]
      } else {
        return range
      }
    }

    const y1_range = getRange([...yRange] || [0, 1], axis1domain)
    const x_range = getRange([...xRange] || [0, 1], xaxisdomain)

    const x = this.getXScale(zoomRanges?.x || x_range, 0, width)
    const y = this.getYScale(zoomRanges?.y || y1_range, height, 0)

    const series = this.getSeries(x, y, joinedData, seriesConfigs, timeExtractor, timeRange)

    // Stash axes for findDataForPoint.
    this.x = x
    this.y = y

    return (
      <AbstractPlot
        clipID={this.clipID}
        width={width}
        height={height}
        margin={margin}
        timeExtractor={null}
        xNotTime={true}
        x={x}
        xAxisLabel={xAxisLabel}
        xFormatter={(x) => x.toString()}
        showY1={!!y}
        y1={y}
        y1AxisLabel={yAxisLabel}
        showY2={false}
        y2={null}
        y2AxisLabel={y2AxisLabel}
        zoomKey={zoomKey}
        baseTime={baseTime}
        showGraphTitle={false}
        graphKey={graphKey}
        graphTitleSupplement={graphTitleSupplement}
        titleFontSize={titleFontSize}
        showSVGGridlines={(graphKey?.gridlines || false) && useSVG}
        isSuppPlot={isSuppPlot}
        tickNum={tickNum}
        PlotCanvas={
          <>
            {!useSVG && !loading && (
              <SeriesPlotCanvas
                ref={canvasRef}
                width={width}
                x={x}
                y={y}
                gridlines={graphKey && graphKey.gridlines}
                height={height}
                margin={margin}
                dataSeries={series}
              />
            )}
          </>
        }
        PlotSVG={<>{useSVG && series.map((series, index) => <SeriesLayer key={index} {...series} />)}</>}
        download={download}
        loading={loading}
        showActionsAnnotations={false} // We can't show actions on a plot without a timeseries based X-Axis
        actions={[]} // We can't show actions on a plot without a timeseries based X-Axis
        site_actions={[]} // We can't show actions on a plot without a timeseries based X-Axis
        annotations={[]} // We can't show actions on a plot without a timeseries based X-Axis
        onCrossHairClick={null} // There's no 'on-click' interactivity on this type of plot
        findDataForPoint={this.findDataForPoint}
        forceReloadPlotData={forceReloadPlotData}
        errorLayers={errorLayers}
      />
    )
  }
}

export default forwardRef((props, ref) => <SeriesPlot canvasRef={ref} {...props} />)
